// IMPORTS

// ANGULAR CORE MODULES
import { Injectable } from "@angular/core";

// CONFIG
import { appSetting } from 'config/appSettings';
import DesktopConfig from 'modules/desktop/services/config';
import { environment } from "environments/environment";
import { osVersions } from 'modules/desktop/constants/osVersion';

// SERVICES
import { EreaderEventService } from "./eReader-event.service";
import { NetwordDetectionService } from "./network-detection.service";
import { ApiGatewayHelperService } from "./apigateway-helper.service";

// EXTERNAL LIBRARIES
import cavClient from 'libs-cavalier-integration/frontend';
import { customAlphabet, nanoid } from 'nanoid';
import testComponentsList from "libs-frontend-micro-base/data/test-components-list/{{=CONFIG_APP_TARGET=}}.json";

const custNanoId = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ', 10);


@Injectable()
export class EventService {

    // CONSTANTS
    CONSTANTS = {
        PLATFORM: {
            DESKTOP: 'Desktop',
            WEBSITE: 'Website'
        },
        DA_EVENTS: {
            EREADER_LAUNCHED: 'EreaderLaunched',
            EREADER_EXITED: 'EreaderExited',
            DOWNLOAD_STARTED: 'GranularDownloadStarted',
            DOWNLOAD_COMPLETED: 'GranularDownloadCompleted',
            SESSION_INITIATED: 'SessionInitiated',
            SESSION_ENDED: 'SessionEnded'
        },
        EREADER_EVENTS: {
            HOTSPOT_USED: 'HotspotUsed',
            HOTSPOT_OPENED: 'HotspotOpened',
            HOTSPOT_CLOSED: 'HotspotClosed',
            TOOL_USED: 'ToolUsed',
            EBOOK_PAGE_VIEWED: 'EbookPageViewed',
            EBOOK_PAGE_VIEWED_STARTED: 'EbookPageViewedStarted',
            EBOOK_PAGE_VIEWED_ENDED: 'EbookPageViewedEnded'
        },
        USER_ACTION_PERFORMED: 'UserActionPerformed',
        EVENT_DATA_CONNECTION: {
            ONLINE: 'online',
            OFFLINE: 'offline'
        },
        OS: {
            WINDOWS: 'windows',
            MAC: 'mac',
            LINUX: 'linux'
        },
        SUB_SYSTEMS: {
            CambridgeOne: 'c1'
        },
        APP: 'foc'
    };

    // VARIABLES

    // flag to identify if the book is blocked
    isBookBlocked = false;

    // Event service instance - web or desktop
    eventServiceInstance;

    // flag to identify if the user is online or offline
    isOnline = true

    // Represents the current OS and OS version of the user
    osInfo = {
        currentOS: null,
        currentOsVersion: null
    }

    // Represents the current schema version
    schemaVersion = 1;

    // Represents the current user data
    userData = {
        extUserId: null,
        userRole: null,
        country: null
    }

    // Represents the last visited page
    lastVisitedPage;
    currentPage;
    navigationFromContext;

    // a unique session id generate everytime a user logs in by entering username and password
    loginSessionId;

    // a unique id generated in three conditions
    //  - If someone enters credentials online and logs in
    //  - If someone launches online and are automatically logged in in online mode
    //  - If someone launches offline and are automatically logged in in offline mode
    launchSessionId;

    // object to store the event data of the LO hotspot used
    // so that we can use it to send the data with the timespent value on close
    loHotspotEventData:any = {};

    // object to store the data of the audio hotspot used
    // so that we can use it to send the data with the timespent value on close
    audioHotspotEventData:any = {};

    // object to store the data of the video hotspot used
    // so that we can use it to send the data with the timespent value on close
    videoHotspotEventData:any = {};

    // object to store the data of the zoom hotspot used
    // so that we can use it to send the data with the timespent value on close
    zoomHotspotEventData:any = {};

    // object to store the data of the page viewed event
    // so that we can use it to send the data with the timespent value on close
    pageViewedEventData:any = {};

    // The browser name
    browser = null;

    constructor(
        private readonly apiGatewayHelperService: ApiGatewayHelperService,
        private readonly networkService: NetwordDetectionService,
        private readonly ereaderEventService: EreaderEventService,
    ) {
        this._addAuthTokenForDataAPICalls = this._addAuthTokenForDataAPICalls.bind(this);
        this._initializeCavalier();
        this.browser = this._getBrowserName();
    }

    // PUBLIC METHODS

    /**
     * Initializes the event service.
     * 
     * @param platform - The platform on which the service is being initialized.
     * @param userData - The user data containing userId, role, and country.
     * @param loginSessionId - The login session ID.
     * @returns The new login session ID.
     */
    init(platform, { userId, role, country = undefined }, bookData) {
        const blockedPrefixes = ["cqa", "mqa", "oqa"];
        const code = bookData.code?.toLowerCase() || "";
        const title = bookData.title?.toLowerCase() || "";

        this.isBookBlocked = testComponentsList.includes(bookData.code) ||
            blockedPrefixes.some(prefix => code.startsWith(prefix) || title.startsWith(prefix));

        this._setUserData(userId, role, country);
        const newLoginSessionId = this._updateLoginSessionId();
        this._updateLaunchSessionId();

        this.eventServiceInstance = this.ereaderEventService;
        this.eventServiceInstance.setCavClient(cavClient);

        return newLoginSessionId;
    }

    /**
     * Resets the user data and session information.
     */
    reset() {
        this._resetUserData();
        this._resetSessionInfo();
    }

    /**
     * Publishes an event based on the given type and data.
     * 
     * @param {string} type - The type of the event.
     * @param {any} data - The data associated with the event.
     * @returns {any} - The result of publishing the event.
     */
    publishEvent(type, data) {
        if (this.isBookBlocked) {
            console.log(`Event Service: Book has been blocked from sending events to Cavalier`);
            return;
        }

        switch (type) {
            case this.CONSTANTS.DA_EVENTS.EREADER_LAUNCHED:
                return this._publishEreaderStartEvent(data);
            case this.CONSTANTS.DA_EVENTS.EREADER_EXITED:
                return this._publishEreaderExitEvent(data);
            case this.CONSTANTS.DA_EVENTS.DOWNLOAD_STARTED:
                return this._publishDownloadStartEvent(data);
            case this.CONSTANTS.DA_EVENTS.DOWNLOAD_COMPLETED:
                return this._publishDownloadCompleteEvent(data);
            case this.CONSTANTS.DA_EVENTS.SESSION_INITIATED:
                return this._publishSessionStartEvent(data);
            case this.CONSTANTS.DA_EVENTS.SESSION_ENDED:
                return this._publishSessionCompleteEvent(data);
            case this.CONSTANTS.EREADER_EVENTS.HOTSPOT_OPENED:
                return this._publishHotspotOpenedEvent(data);
            case this.CONSTANTS.EREADER_EVENTS.HOTSPOT_CLOSED:
                return this._publishHotspotClosedEvent(data);
            case this.CONSTANTS.EREADER_EVENTS.TOOL_USED:
                return this._publishToolsLaunchEvent(data);
            case this.CONSTANTS.EREADER_EVENTS.EBOOK_PAGE_VIEWED_STARTED:
                return this._publishEbookPageViewedStartedEvent(data);
            case this.CONSTANTS.EREADER_EVENTS.EBOOK_PAGE_VIEWED_ENDED:
                return this._publishEbookPageViewedEndedEvent(data);
            case this.CONSTANTS.USER_ACTION_PERFORMED:
                return this._publishUserActionPerformedEvent(data);
            default:
                console.warn("Error: Invalid event type");
        }
    }

    /**
     * Synchronizes the event data from local to server.
     * 
     * @param forceSync - Optional parameter to force synchronization.
     */
    sync({ forceSync = false, userId = null } = {}) {
        this.eventServiceInstance.sync({ forceSync, userId });
    }

    /**
     * Generates a unique download session ID using custNanoId.
     * @returns {string} The generated download session ID.
     */
    generateDownloadSessionId() {
        return custNanoId();
    }

    // PRIVATE METHODS

    // EVENT PUBLISHING METHODS

    /**
     * Publishes the e-reader start event.
     * 
     * @param data - The data for the event.
     */
    _publishEreaderStartEvent(data) {
        let ereaderData: any = this._prepareContentEventData(data);
        ereaderData = {
            ...ereaderData,
            'fact_name': this.CONSTANTS.DA_EVENTS.EREADER_LAUNCHED,
            'navigated_from': data.navigatedFrom,
        }

        this.eventServiceInstance.publishEvent(ereaderData);
    }

    /**
     * Publishes the e-reader exit event.
     * 
     * @param data - The data for the event.
     */
    _publishEreaderExitEvent(data) {
        let ereaderData: any = this._prepareContentEventData(data);
        ereaderData = {
            ...ereaderData,
            'fact_name': this.CONSTANTS.DA_EVENTS.EREADER_EXITED,
            'navigated_to': data.navigatedTo,
        }

        this.eventServiceInstance.publishEvent(ereaderData);
    }

    /**
     * Publishes a download start event.
     * 
     * @param data - The data for the download event.
     */
    _publishDownloadStartEvent(data) {
        let downloadData: any = this._prepareDownloadEventData(data);
        downloadData = {
            ...downloadData,
            'fact_name': this.CONSTANTS.DA_EVENTS.DOWNLOAD_STARTED,
        }

        this.eventServiceInstance.publishEvent(downloadData);
    }

    /**
     * Publishes a download complete event.
     * @param data - The data associated with the download.
     */
    async _publishDownloadCompleteEvent(data) {
        let downloadData: any = this._prepareDownloadEventData(data);
        downloadData = {
            ...downloadData,
            'fact_name': this.CONSTANTS.DA_EVENTS.DOWNLOAD_COMPLETED,
        }

        await this.eventServiceInstance.publishEvent(downloadData);
    }

    /**
     * Publishes a session start event.
     * 
     * @param data - The data for the session start event.
     */
    _publishSessionStartEvent(data) {
        let sessionData: any = this._prepareSessionEventData(data);
        sessionData = {
            ...sessionData,
            'fact_name': this.CONSTANTS.DA_EVENTS.SESSION_INITIATED,
        }

        this.eventServiceInstance.publishEvent(sessionData);
    }

    /**
     * Publishes a session complete event.
     * @param data - The data for the event.
     */
    async _publishSessionCompleteEvent(data) {
        let sessionData: any = this._prepareSessionEventData(data);
        sessionData = {
            ...sessionData,
            'fact_name': this.CONSTANTS.DA_EVENTS.SESSION_ENDED,
            'action': data.action
        }

        await this.eventServiceInstance.publishEvent(sessionData);
    }

    /**
     * Publishes a hotspot used event.
     * 
     * @param {object} data - The data for the event.
     */
    _publishHotspotOpenedEvent(data) {

        const baseData = this._prepareBaseEventData();
        const eventData = {
            ...baseData,
            fact_id: `${this.userData.extUserId}~~${this._getPrefixedUniqueId(16, "")}`,
            organisation_id: data.organisationId,
            fact_name: this.CONSTANTS.EREADER_EVENTS.HOTSPOT_USED,
            component_id: data.productCode,
            component_type: data.productType,
            hotspot_id: data.moduleId,
            type: data.type,
            sub_type: data.type == 'learning-object' ? data.sub_type : null,
            mode: this.isOnline ? "online" : "offline",
            content_downloaded: data.isBookDownloaded,
            browser: this.browser,
        };

        // if the hotspot is a learning object, store the data in a variable
        // so that we can use it to send the event later when the LO closes
        // and we can calculate the time spent inside the LO
        if (data.type == 'learning-object') {
            this.loHotspotEventData = eventData;
        } else if (data.type == 'audio' || data.type == 'audio-package') {
            this.audioHotspotEventData = eventData;
            this.audioHotspotEventData.type = 'audio';
        } else if (data.type == 'video' || data.type == 'video-package') {
            this.videoHotspotEventData = eventData;
            this.videoHotspotEventData.type = 'video';
        } else if(data.type == 'predefined-zoom') {
            this.zoomHotspotEventData = eventData;
            this.zoomHotspotEventData.type = 'predefined-zoom';
        } else {
            this.eventServiceInstance.publishEvent({ ...eventData, type: this._getEventType(data.type) });
        }
    }

    /**
     * Returns the corresponding event type string based on the provided type.
     *
     * @param type - The type of event to be converted.
     * @returns The corresponding event type string. If the type is 'page', it returns 'jump_to_page'.
     * If the type is 'url', it returns 'link'. For any other type, it returns the type itself.
     */
    _getEventType(type) {
        switch (type) {
            case 'page':
                return 'jump_to_page';
            case 'url':
                return 'link';
            default:
                return type;
        }
    }

    /**
     * Retrieves the hotspot event data based on the provided type.
     *
     * @param type - The type of the hotspot.
     * @returns The corresponding hotspot event data for the given type.
     */
    _getHotspotType(type) {
        switch (type) {
            case 'learning-object':
                return this.loHotspotEventData;
            case 'audio':
                return this.audioHotspotEventData;
            case 'video':
                return this.videoHotspotEventData;
            case 'predefined-zoom':
                return this.zoomHotspotEventData;
        }
    }

    /**
     * Resets the hotspot data based on the provided type.
     * 
     * @param type - The type of hotspot data to reset.
     */
    _resetHotspotData(type) {
        switch (type) {
            case 'learning-object':
                this.loHotspotEventData = {};
                break;
            case 'audio':
                this.audioHotspotEventData = {};
                break;
            case 'video':
                this.videoHotspotEventData = {};
                break;
            case 'predefined-zoom':
                this.zoomHotspotEventData = {};
        }
    }

    /**
     * Publishes a LO, audio and video hotspot used event.
     * 
     * This method calculates the time spent on the hotspot by subtracting the initial timestamp
     * from the close timestamp provided in the data parameter.The calculated time spent
     * is then added to the event data, which is subsequently published using the event service instance.
     * 
     * @param {object} data - The data for the event.
     */
    _publishHotspotClosedEvent(data) {
        const hotspotEventData = this._getHotspotType(data.type);

        if(!hotspotEventData || !Object.keys(hotspotEventData).length) {
            return;
        }

        const eventData = {
            ...hotspotEventData,
            time_spent: data.closeTimestamp - hotspotEventData.timestamp
        }

        this._resetHotspotData(data.type);
        this.eventServiceInstance.publishEvent(eventData);
    }

    /**
     * Publishes a tools launch event.
     * 
     * @param data - The data for the event.
     */
    _publishToolsLaunchEvent(data) {
        const baseData = this._prepareBaseEventData();
        const eventData = {
            ...baseData,
            fact_id: `${this.userData.extUserId}~~${this._getPrefixedUniqueId(16, "")}`,
            organisation_id: data.organisationId,
            fact_name: this.CONSTANTS.EREADER_EVENTS.TOOL_USED,
            component_id: data.productCode,
            component_type: data.productType,
            tool: data.type,
            interaction: data.interaction,
            interaction_count: data.count,
            mode: this.isOnline ? "online" : "offline",
            content_downloaded: data.isBookDownloaded,
            browser: this.browser,
            ...(data.time_spent && { time_spent: data.time_spent })
        };

        if(data.type == 'change_book' || data.type == 'zoom' || data.type == 'assignment' || data.type == 'change_page' || data.type == 'note' || data.type == 'mask' || data.type == 'spotlight' || data.type == 'moveToolbar' || data.type == 'layout' || data.type == 'timer' || data.type == 'drawing') {
            this.eventServiceInstance.publishEvent(eventData);
        }
    }

    /**
     * Publishes the page viewed start event.
     * 
     * @param data - The data for the event.
     */
    _publishEbookPageViewedStartedEvent(data) {
        const baseData = this._prepareBaseEventData();
        this.pageViewedEventData = {
            ...baseData,
            fact_id: `${this.userData.extUserId}~~${this._getPrefixedUniqueId(16, "")}`,
            fact_name: this.CONSTANTS.EREADER_EVENTS.EBOOK_PAGE_VIEWED,
            organisation_id: data.organisationId,
            component_id: data.productCode,
            component_type: data.productType,
            page_context: data.bookType,
            network_mode: this.isOnline ? "online" : "offline",
            content_downloaded: data.isBookDownloaded,
            url: data.url,
            browser: this.browser
        };

    }
    
    /**
     * Publishes the page viewed end event.
     * 
     * @param data - The data for the event.
    */
    _publishEbookPageViewedEndedEvent(data) {
        if (!data?.closeTimestamp || !this.pageViewedEventData?.timestamp) {
            return;
        }

        const eventData = {
            ...this.pageViewedEventData,
            time_spent: data.closeTimestamp - this.pageViewedEventData.timestamp
        };

        this.pageViewedEventData = {};
        this.eventServiceInstance.publishEvent(eventData);
    }

    /**
     * Publishes the user action performed event.
     * 
     * @param data - The data for the event.
     */
    _publishUserActionPerformedEvent(data) {
        const eventData = {
            ...this._prepareUserActionPerformedEventData(),
            feature_name: data.feature,
            organisation_id: data.organisationId,
            context: data.context,
            initial_value: data.initialValue,
            action_type: data.actionType
        };

        this.eventServiceInstance.publishEvent(eventData);
    }

    /**
     * Prepares the event data for user action performed events.
     * @returns {Object} - The prepared base event data for user action performed events.
     */
    _prepareUserActionPerformedEventData() {
        const baseData = this._prepareBaseEventData();
        return {
            ...baseData,
            fact_id: `${this.userData.extUserId}~~${this._getPrefixedUniqueId(16, "")}`,
            fact_name: this.CONSTANTS.USER_ACTION_PERFORMED,
            user_agent: window.navigator.userAgent,
            host: window.location.host,
            url: window.location.href,
            web_page_title: window.document.title,
            network_mode: this.isOnline ? this.CONSTANTS.EVENT_DATA_CONNECTION.ONLINE : this.CONSTANTS.EVENT_DATA_CONNECTION.OFFLINE,
            sub_system: this.CONSTANTS.SUB_SYSTEMS.CambridgeOne,
            app: this.CONSTANTS.APP
        }
    }

    /**
     * Prepares the content event data.
     * 
     * @param {any} data - The data to be prepared.
     * @returns {object} - The prepared content event data.
     */
    _prepareContentEventData(data) {
        const baseData = this._prepareBaseEventData();
        return {
            ...baseData,
            'component_id': data.productCode,
            'component_type': data.productType,
            'mode': this.isOnline ? this.CONSTANTS.EVENT_DATA_CONNECTION.ONLINE : this.CONSTANTS.EVENT_DATA_CONNECTION.OFFLINE,
            'downloaded': data.isBookDownloaded,
            'context': this.getNavigationFromContext(),
        }
    }

    /**
     * Prepares the event data for download.
     * 
     * @param {object} data - The data object containing information for the download event.
     * @returns {object} - The prepared event data for download.
     */
    _prepareDownloadEventData(data) {
        const baseData = this._prepareBaseEventData();
        return {
            ...baseData,
            'component_id': data.productCode,
            'component_version': data.version,
            'component_type': data.productType,
            'component_sub_type': data.subType,
            'download_session_id': data.downloadSessionId,
            'smart_download': data.isSmartDownload,
            'is_all_content': data.isAllContent,
            'complete_download': data.completeDownload,
            'units': data.selectedUnitsCount,
            'action': 'download'
        }
    }

    /**
     * Prepares the session event data.
     * 
     * @param {any} data - The data to be included in the event.
     * @returns {object} - The prepared session event data.
     */
    _prepareSessionEventData(data) {
        const baseData = this._prepareBaseEventData();
        return {
            ...baseData,
            'mode': this.isOnline ? this.CONSTANTS.EVENT_DATA_CONNECTION.ONLINE : this.CONSTANTS.EVENT_DATA_CONNECTION.OFFLINE,
            'auto_login': data.autoLogin,
        }
    }

    /**
     * Prepares the base event data object.
     * @returns The base event data object.
     */
    _prepareBaseEventData() {
        return {
            'timestamp': Date.now(),
            'user_id': this.userData.extUserId,
            'user_dls_id': this.userData.extUserId,
            'user_role': this.userData.userRole,
            'user_country': this.userData.country,
            'session_id': this._getSessionId(),
            'app_version': environment.isDesktopApp ? DesktopConfig.app.version : null,
            'schema_version': this.schemaVersion,
            'os': this.osInfo.currentOS,
            'os_version': this.osInfo.currentOsVersion,
            'delivery_platform': environment.isDesktopApp ? this.CONSTANTS.PLATFORM.DESKTOP : this.CONSTANTS.PLATFORM.WEBSITE,
        }
    }

    // SETUP METHODS

    /**
     * Initializes the Cavalier client based on the current environment.
     * If the environment is a desktop app, it initializes the client with desktop app options.
     * If the environment is foc, it initializes the client with default options.
     */
    _initializeCavalier() {
        const appName = environment.isDesktopApp ? 'desktopApp' : 'foc';
        const cavOptions = environment.isDesktopApp ? {
            appDomain: environment.apiDomain,
            getQueryParams: this._addAuthTokenForDataAPICalls,
            disableCSRFToken: true
        } : {};

        cavClient.init(appName, cavOptions);
    }

    /**
     * Sets the network status and starts monitoring it.
     * @returns {Promise<void>} A promise that resolves when the network status is set and monitoring is started.
     */
    async _setNetworkStatusAndMonitor() {
        this.isOnline = await this.networkService.isOnline();
        this.networkService.monitorStatus().subscribe(async (isOnline) => {
            if (this.isOnline != isOnline) {
                this.isOnline = isOnline;
            }
        });
    }

    /**
     * Initializes the OS information by determining the current operating system and its version.
     */
    async _initializeOSInfo() {
        let os = await DLS_IPCCallers.GET_CURRENT_OS();

        if (os == 'win32') {
            this.osInfo.currentOS = this.CONSTANTS.OS.WINDOWS;
        } else if (os == 'darwin') {
            this.osInfo.currentOS = this.CONSTANTS.OS.MAC;
        } else {
            this.osInfo.currentOS = this.CONSTANTS.OS.LINUX;
        }

        // using userAgent to find the OS version of the user
        let osDetail = navigator.userAgent;
        let osVer;
        if (osDetail.indexOf("Windows") != -1) {
            osVer = (osVersions.Windows.find(ver => (osDetail.indexOf(ver.userAgent) != -1)) || {});
        } else if (osDetail.indexOf('Mac') != -1) {
            osVer = (osVersions.Mac.find(ver => (osDetail.indexOf(ver.userAgent) != -1)) || {});
        } else {
            osVer = osVersions.Linux[0];
        }
        this.osInfo.currentOsVersion = osVer?.osVersion;
    }

    /**
     * Adds an authentication token for data API calls.
     * If the application is running as a desktop app, it retrieves the API auth token using the `apiGatewayHelperService`.
     * 
     * @returns {Promise<string>} A promise that resolves to the authentication token.
     */
    async _addAuthTokenForDataAPICalls() {
        let token;
        if (environment.isDesktopApp) {
            token = await this.apiGatewayHelperService.getAPIAuthToken();
        }
        return token;
    }


    // HELPER METHODS

    /**
     * Generates a unique ID with a given prefix.
     * 
     * @param {number} chars - The number of characters in the unique ID.
     * @param {string} prefix - The prefix to be added to the unique ID.
     * @returns {string} - The unique ID with the prefix.
     */
    _getPrefixedUniqueId(chars, prefix) {
        const uniqueId = nanoid(chars);
        return prefix + uniqueId;
    }

    /**
     * Gets the browser name.
     * @returns The browser name.
     */
    _getBrowserName() {
        const userAgent = navigator.userAgent;
      
        // Check for Opera or Opera Next
        if (/OPR\//.test(userAgent)) {
          return 'Opera';
        }
      
        // Check for Edge
        if (/Edge\//.test(userAgent)) {
          return 'Edge';
        }
      
        // Check for Chrome
        if (/Chrome\//.test(userAgent) && !/Edge\//.test(userAgent)) {
          return 'Chrome';
        }
      
        // Check for Firefox
        if (/Firefox\//.test(userAgent)) {
          return 'Firefox';
        }
      
        // Check for Safari
        if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) {
          return 'Safari';
        }
      
        // Check for IE
        if (/MSIE|Trident\//.test(userAgent)) {
          return 'Internet Explorer';
        }
      
        // Unknown browser
        return null;
    }

    // DATA METHODS

    /**
     * Sets the user data.
     * @param {string} uid - The user ID.
     * @param {string} userRole - The user role.
     * @param {string|null} country - The country (optional).
     */
    _setUserData(uid, userRole, country = null) {
        this.userData.extUserId = uid;
        this.userData.userRole = userRole;
        this.userData.country = country;
    }

    /**
     * Resets the user data and navigation properties.
     */
    _resetUserData() {
        this.userData = {
            extUserId: undefined,
            userRole: undefined,
            country: undefined
        };
        this.lastVisitedPage = undefined;
        this.currentPage = undefined;
        this.navigationFromContext = undefined;
    }

    /**
     * Sets the last visited page.
     * 
     * @param {string} lastVisitedPage - The last visited page.
     */
    setLastVisitedPage(lastVisitedPage) {
        this.lastVisitedPage = lastVisitedPage;
    }

    /**
     * Retrieves the last visited page.
     * 
     * @returns The last visited page.
     */
    getLastVisitedPage() {
        return this.lastVisitedPage;
    }

    /**
     * Sets the current page.
     * 
     * @param currentPage - The current page to set.
     */
    setCurrentPage(currentPage) {
        this.currentPage = currentPage;
    }

    /**
     * Retrieves the current page.
     * 
     * @returns The current page.
     */
    getCurrentPage() {
        return this.currentPage;
    }

    /**
     * Sets the navigation context from the given context.
     * @param {any} context - The navigation context.
     */
    setNavigationFromContext(context) {
        this.navigationFromContext = context;
    }

    /**
     * Retrieves the navigation from the context.
     * @returns The navigation from the context.
     */
    getNavigationFromContext() {
        return this.navigationFromContext;
    }

    /**
     * Gets the session ID by combining the login session ID and launch session ID.
     * If either login session ID or launch session ID is absent, it logs a warning and returns undefined.
     * @returns The combined session ID or undefined if either login session ID or launch session ID is absent.
     */
    _getSessionId() {
        if (this.loginSessionId && this.launchSessionId) {
            return `${this.loginSessionId}-${this.launchSessionId}`;
        }

        console.warn("Error: Login/Launch SessionId absent!");
    }

    /**
     * Updates the login session ID.
     * 
     * @param sessionId - The session ID to update. If not provided, a new session ID will be generated.
     * @returns The updated login session ID.
     */
    _updateLoginSessionId(sessionId?) {
        this.loginSessionId = sessionId || custNanoId();
        return this.loginSessionId;
    }

    /**
     * Updates the launch session ID.
     * @returns The updated launch session ID.
     */
    _updateLaunchSessionId() {
        this.launchSessionId = custNanoId();
        return this.launchSessionId;
    }

    /**
     * Resets the session information by setting the loginSessionId and launchSessionId to null.
     */
    _resetSessionInfo() {
        this.loginSessionId = null;
        this.launchSessionId = null;
    }
}